import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  Rating,
  Typography,
} from '@mui/material';
import { Task } from '../../../types';
import { TaskFormWrapper } from './TaskFormWrapper';

interface BasicCaseAssessmentFormProps {
  task: Task;
  onSubmit: (formData: any) => void;
}

export const BasicCaseAssessmentForm: React.FC<BasicCaseAssessmentFormProps> = ({
  task,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState({
    caseStrength: 3,
    jurisdictionAssessment: '',
    statuteOfLimitations: '',
    potentialDefenses: '',
    evidenceStrength: 3,
    damagesEstimate: '',
    liabilityAssessment: '',
    keyWitnesses: '',
    expertNeeded: false,
    expertTypes: '',
    conflictCheckPassed: false,
    ethicalConcerns: '',
    resourceRequirements: '',
    recommendedAction: '',
    additionalNotes: '',
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <TaskFormWrapper task={task} onSubmit={() => onSubmit(formData)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography component="legend">Case Strength Assessment</Typography>
            <Rating
              value={formData.caseStrength}
              onChange={(_, value) => handleChange('caseStrength', value)}
              max={5}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Jurisdiction Assessment</InputLabel>
            <Select
              value={formData.jurisdictionAssessment}
              label="Jurisdiction Assessment"
              onChange={(e) => handleChange('jurisdictionAssessment', e.target.value)}
            >
              <MenuItem value="favorable">Favorable</MenuItem>
              <MenuItem value="neutral">Neutral</MenuItem>
              <MenuItem value="unfavorable">Unfavorable</MenuItem>
              <MenuItem value="complex">Complex/Multiple</MenuItem>
              <MenuItem value="uncertain">Uncertain</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Statute of Limitations"
            fullWidth
            required
            value={formData.statuteOfLimitations}
            onChange={(e) => handleChange('statuteOfLimitations', e.target.value)}
            placeholder="Relevant deadlines and time constraints"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Potential Defenses"
            fullWidth
            multiline
            rows={2}
            value={formData.potentialDefenses}
            onChange={(e) => handleChange('potentialDefenses', e.target.value)}
            placeholder="Anticipated defenses and counter-arguments"
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography component="legend">Evidence Strength</Typography>
            <Rating
              value={formData.evidenceStrength}
              onChange={(_, value) => handleChange('evidenceStrength', value)}
              max={5}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Damages Estimate"
            fullWidth
            value={formData.damagesEstimate}
            onChange={(e) => handleChange('damagesEstimate', e.target.value)}
            placeholder="Preliminary estimate of potential damages"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Liability Assessment</InputLabel>
            <Select
              value={formData.liabilityAssessment}
              label="Liability Assessment"
              onChange={(e) => handleChange('liabilityAssessment', e.target.value)}
            >
              <MenuItem value="clear">Clear Liability</MenuItem>
              <MenuItem value="probable">Probable Liability</MenuItem>
              <MenuItem value="questionable">Questionable Liability</MenuItem>
              <MenuItem value="unlikely">Unlikely Liability</MenuItem>
              <MenuItem value="complex">Complex/Multiple Parties</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Key Witnesses"
            fullWidth
            multiline
            rows={2}
            value={formData.keyWitnesses}
            onChange={(e) => handleChange('keyWitnesses', e.target.value)}
            placeholder="List potential key witnesses and their relevance"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.expertNeeded}
                onChange={(e) => handleChange('expertNeeded', e.target.checked)}
              />
            }
            label="Expert Witness Needed"
          />
        </Grid>

        {formData.expertNeeded && (
          <Grid item xs={12}>
            <TextField
              label="Expert Types Needed"
              fullWidth
              multiline
              rows={2}
              value={formData.expertTypes}
              onChange={(e) => handleChange('expertTypes', e.target.value)}
              placeholder="Specify types of experts needed"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.conflictCheckPassed}
                  onChange={(e) => handleChange('conflictCheckPassed', e.target.checked)}
                />
              }
              label="Conflict Check Passed"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Ethical Concerns"
            fullWidth
            multiline
            rows={2}
            value={formData.ethicalConcerns}
            onChange={(e) => handleChange('ethicalConcerns', e.target.value)}
            placeholder="Note any potential ethical issues or concerns"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Resource Requirements"
            fullWidth
            multiline
            rows={2}
            value={formData.resourceRequirements}
            onChange={(e) => handleChange('resourceRequirements', e.target.value)}
            placeholder="Estimate required resources (time, staff, costs)"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Recommended Action</InputLabel>
            <Select
              value={formData.recommendedAction}
              label="Recommended Action"
              onChange={(e) => handleChange('recommendedAction', e.target.value)}
            >
              <MenuItem value="accept">Accept Case</MenuItem>
              <MenuItem value="decline">Decline Case</MenuItem>
              <MenuItem value="further-review">Needs Further Review</MenuItem>
              <MenuItem value="refer">Refer to Another Firm</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Additional Notes"
            fullWidth
            multiline
            rows={3}
            value={formData.additionalNotes}
            onChange={(e) => handleChange('additionalNotes', e.target.value)}
            placeholder="Any additional observations or concerns"
          />
        </Grid>
      </Grid>
    </TaskFormWrapper>
  );
};